import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Card } from '../components/ui/Card';
import { ChevronDown } from 'lucide-react';

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: "What is Wallstreetle?",
    answer: "Wallstreetle is the ultimate stock wordle and finance wordle - a free daily stock guessing game where you play a guess the stock game using financial charts, ticker guessing, and metrics. This equity game and investing puzzle features multiple market categories including SPY, Tech, NASDAQ, and WallStreetBets. Join the daily stock challenge and master stock trivia!"
  },
  {
    question: "How do you play the stock guessing game?",
    answer: "To play Wallstreetle's stock guessing game: 1) Choose a category (SPY, Tech, etc.), 2) View the daily stock chart and financial metrics, 3) Use hints if needed, 4) Guess the stock by typing the company name or ticker symbol, 5) Win by guessing correctly with fewer hints and guesses!"
  },
  {
    question: "Is Wallstreetle a free stock market game?",
    answer: "Yes! Wallstreetle is completely free to play. You get access to 4 free stock categories: Household Name, Technology, Financial Services, and WallStreetBets. Premium categories are available with a subscription for even more daily puzzles."
  },
  {
    question: "What stock categories are available?",
    answer: "Wallstreetle offers multiple stock puzzle categories including NASDAQ 100, SPY (S&P 500), Tech, Pharma, WallStreetBets, Foreign stocks, and more. Each category features different companies and daily puzzles."
  },
  {
    question: "How often are new stock puzzles released?",
    answer: "New daily stock puzzles are released every day at 2:00 AM UTC across all categories. Each category has its own unique stock to guess, so you can play multiple puzzles per day!"
  },
  {
    question: "Can I play yesterday's stock puzzle?",
    answer: "Wallstreetle focuses on daily stock guessing - once the day changes, a new stock puzzle is available. However, your game history and statistics are saved, so you can track your performance over time."
  },
  {
    question: "What makes Wallstreetle different from other stock games?",
    answer: "Wallstreetle combines education with entertainment! Unlike other stock market games, we provide real financial metrics, charts, and smart hints to help you learn about stocks while playing. It's the perfect way to improve your stock market knowledge through daily practice."
  },
  {
    question: "How do hints work in the stock guessing game?",
    answer: "Wallstreetle offers multiple hint types: 1) Company clues (industry, business model), 2) Financial hints (revenue, market cap ranges), 3) Historical context, and 4) Additional clues. Each hint makes the puzzle easier but affects your final score and leaderboard ranking."
  },
  {
    question: "Can I play Wallstreetle on mobile?",
    answer: "Yes! Wallstreetle is fully responsive and works on all devices - desktop, tablet, and mobile. Play the stock guessing game anywhere, anytime on your preferred device."
  },
  {
    question: "Are the stock charts and data real?",
    answer: "Yes! Wallstreetle uses real market data from TwelveData and other financial APIs to provide accurate stock charts, prices, and financial metrics. You're learning with real-world stock information."
  },
  {
    question: "How do leaderboards work?",
    answer: "Leaderboards rank players based on their performance in each category. Your score is calculated from: number of guesses, hints used, and time to solve. Compete globally or create private leaderboards with friends!"
  },
  {
    question: "What is the best strategy for the stock guessing game?",
    answer: "Start by analyzing the stock chart pattern and price range. Use financial metrics like market cap and revenue to narrow down the sector. Look at the chart's volatility - stable charts often indicate blue-chip stocks, while volatile ones might be growth or meme stocks. Use hints strategically!"
  },
  {
    question: "Is Wallstreetle good for learning about stocks?",
    answer: "Absolutely! Wallstreetle is an excellent educational tool for learning about the stock market. By playing daily, you'll become familiar with major companies, their stock patterns, financial metrics, and market sectors. It's learning disguised as a fun puzzle game!"
  },
  {
    question: "Can I share my results?",
    answer: "Yes! After completing each daily stock puzzle, you can share your results on social media showing your score, guesses, and category. Challenge your friends to beat your score!"
  },
  {
    question: "What are premium categories?",
    answer: "Premium categories include Financial Services, Consumer Goods, Pharmaceuticals, Energy, Industrial, and Foreign stocks. These unlock with a Wallstreetle Premium subscription, giving you access to all 9 categories for more daily puzzles."
  }
];

export default function FAQPage() {
  const [openIndex, setOpenIndex] = React.useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // FAQ Schema structured data with WebPage wrapper for better context
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "name": "Stock Guessing Game FAQ - Wallstreetle Help & Answers",
    "description": "Frequently asked questions about Wallstreetle - the stock wordle and finance wordle. Learn how to play, strategies, and features.",
    "url": "https://wallstreetle.com/faq",
    "inLanguage": "en-US",
    "author": {
      "@type": "Person",
      "name": "Wallstreetle Team",
      "url": "https://wallstreetle.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Wallstreetle",
      "logo": {
        "@type": "ImageObject",
        "url": "https://wallstreetle.com/logo512.png",
        "width": 512,
        "height": 512
      }
    },
    "datePublished": "2024-09-01",
    "dateModified": new Date().toISOString().split('T')[0],
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Wallstreetle | FAQ</title>
        <meta name="description" content="Frequently asked questions about Wallstreetle - the stock wordle and finance wordle. Learn how to play this guess the stock game with ticker guessing, compete on leaderboards, and master the daily stock challenge." />
        <meta name="keywords" content="stock wordle, finance wordle, stock guessing game, guess the stock game, ticker guessing, equity game, stock trivia, investing puzzle, daily stock challenge, stock guessing game faq, how to play stock puzzle, wallstreetle help" />
        <link rel="canonical" href="https://wallstreetle.com/faq" />

        {/* Open Graph */}
        <meta property="og:title" content="Wallstreetle | FAQ" />
        <meta property="og:description" content="Get answers about the stock wordle and finance wordle. Learn how to play this guess the stock game with ticker guessing, strategies, daily stock challenge tips, and more." />
        <meta property="og:url" content="https://wallstreetle.com/faq" />

        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background dark:bg-gray-900 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              Everything you need to know about playing Wallstreetle stock guessing game
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <Card key={index} className="overflow-hidden">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <h2 className="text-lg font-semibold pr-4">
                    {faq.question}
                  </h2>
                  <ChevronDown
                    className={`flex-shrink-0 transition-transform ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                    size={20}
                  />
                </button>
                {openIndex === index && (
                  <div className="px-6 pb-4 text-gray-700 dark:text-gray-300">
                    {faq.answer}
                  </div>
                )}
              </Card>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Card className="p-8">
              <h2 className="text-2xl font-bold mb-4">Still have questions?</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Ready to start playing the best stock guessing game?
              </p>
              <a
                href="/"
                className="inline-block px-8 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors"
              >
                Play Now
              </a>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}
